import React, { FC, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { capitalizeFirstLetter } from "@app/utils";
import { logMsg } from "@app/utils/logMsg";
import { routes } from "@app/routing";
import { UPSAPIContext, UPSContext } from "@app/provider";
import { SpinnerView } from "@app/components";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { PaymentPlan } from "@app/postBill/models/apiControllerModel";
import "./upsCreatePaymentForm.scss";
import PaymentSummaryCard from "@app/postBill/components/paymentSummaryCard";
import { LineItem } from "@app/ups/models";
import { convertFullTestName } from "@app/ups/utils/convertTestName";
import { getFormattedMonthlyPaymentDate } from "@app/postBill/utils";
import { useUPSHeapEvent } from "@app/ups/hooks";
import { HEAP_EVENTS } from "@app/provider/types";

const messages = defineMessages({
  upsPaymentPayButton: {
    id: "upsPaymentPayButton",
    defaultMessage: "Pay now",
  },
  upsPaymentFormDynamicMonthlyPaymentDate: {
    id: "upsPaymentFormDynamicMonthlyPaymentDate",
    defaultMessage: "{day} of each month",
  },
});

const UPSCreatePaymentForm: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { selectedUPSPaymentPlan, selectedUPSPaymentPlanItems } =
    useContext(UPSContext);
  const {
    upsApiProviderState: {
      guestValidationData,
      paymentRequestData,
      paymentSetupData,
    },
    confirmDirectPayment,
    confirmPaymentPlan,
  } = useContext(UPSAPIContext);
  const [loading, setLoading] = useState<boolean>(false);
  const { trackUPSHeapEvent } = useUPSHeapEvent();

  const stripe = useStripe();
  const elements = useElements();

  const handleConfirmPayment = async (paymentIntentId: string) => {
    try {
      await confirmDirectPayment(
        guestValidationData?.paymentRequestId as string,
        {
          paymentIntentId: paymentIntentId,
        }
      );
    } catch {
      logMsg("error", "ups.payment", "Error sending payment choice");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPaymentPlan = async (setupIntentId: string) => {
    try {
      await confirmPaymentPlan(
        guestValidationData?.paymentRequestId as string,
        {
          setupIntentId: setupIntentId,
        }
      );
      navigate(routes.upsCreatePaymentConfirmation, { replace: true });
    } catch {
      logMsg("error", "ups.payment", "Error sending payment plan request");
      navigate(routes.upsCreatePaymentFailure);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentRequest = async () => {
    trackUPSHeapEvent({
      eventKey: HEAP_EVENTS.ups_click_billing_payment_paynow,
    });
    const isDefaultPlan = selectedUPSPaymentPlan === PaymentPlan.None;

    const isReady = stripe && elements && paymentSetupData !== undefined;

    if (!isReady) return;

    setLoading(true);

    try {
      if (isDefaultPlan) {
        const { paymentIntent, error } = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
        });

        if (error) {
          navigate(routes.upsCreatePaymentFailure);
        } else {
          await handleConfirmPayment(paymentIntent.id);
          navigate(routes.upsCreatePaymentConfirmation, { replace: true });
        }
      } else {
        const { setupIntent, error } = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
        });

        if (error) {
          navigate(routes.upsCreatePaymentFailure);
        } else {
          handleConfirmPaymentPlan(setupIntent.id as string);
        }
      }
    } catch {
      logMsg("error", "ups.payment", "Error sending payment request");
      navigate(routes.upsCreatePaymentFailure);
    }
  };

  const getFirstPaymentDate = (): string => {
    return intl.formatMessage(
      messages.upsPaymentFormDynamicMonthlyPaymentDate,
      {
        day: getFormattedMonthlyPaymentDate(intl, new Date()),
      }
    );
  };

  return (
    <>
      <SpinnerView isLoading={loading} />
      <div className="ups-payment-form__container">
        <PaymentElement />
        {(paymentRequestData?.lineItems?.length as number) > 0 && (
          <PaymentSummaryCard
            selectedPlan={selectedUPSPaymentPlan}
            testNames={
              paymentRequestData?.lineItems?.map(
                (item: LineItem) => convertFullTestName(item?.name) as string
              ) as string[]
            }
            testPrices={
              paymentRequestData?.lineItems?.map(
                (item: LineItem) => item?.amountCents * 100
              ) as number[]
            }
            totalPrice={(paymentRequestData?.totalAmountCents as number) * 100}
            currency={paymentRequestData?.currency as string}
            paymentItems={selectedUPSPaymentPlanItems}
            firstPaymentDate={getFirstPaymentDate()}
          />
        )}
        <div className="pay-button">
          <ButtonNoShadow
            raised
            className="ups-payment__button"
            onClick={handlePaymentRequest}
          >
            {intl.formatMessage(messages.upsPaymentPayButton)}
          </ButtonNoShadow>
        </div>
      </div>
    </>
  );
};

export default UPSCreatePaymentForm;
